<template>
  <div class="branding-editor">
    <!-- Display name -->
    <div v-if="!hideDisplayName" class="field-block">
      <v-text-field
        :model-value="modelValue.displayName"
        label="Business display name"
        variant="outlined"
        density="comfortable"
        :rules="[rules.required]"
        :readonly="readonly"
        @update:model-value="update('displayName', $event)"
      >
        <template #append-inner>
          <v-tooltip location="top" max-width="280">
            <template #activator="{ props }">
              <v-icon
                v-bind="props"
                icon="mdi-help-circle-outline"
                size="small"
                class="field-help-icon"
                aria-label="Display name help"
              />
            </template>
            <span>{{ hints.displayName }}</span>
          </v-tooltip>
        </template>
      </v-text-field>
    </div>

    <!-- Logo -->
    <LogoUploader
      v-if="!hideLogo"
      :initial-logo-url="initialLogoUrl"
      :initial-logo-file-name="initialLogoFileName"
      :readonly="readonly"
      :validation-error="validationError"
      @update:logo-file="$emit('update:logoFile', $event)"
      @logo-error="$emit('logo-error', $event)"
    />

    <!-- Colours -->
    <div class="color-row field-block">
      <div class="color-picker-group">
        <div class="block-label-row">
          <label class="block-label">Background colour</label>
          <v-tooltip location="top" max-width="280">
            <template #activator="{ props }">
              <v-icon
                v-bind="props"
                icon="mdi-help-circle-outline"
                size="small"
                class="field-help-icon"
                aria-label="Background colour help"
              />
            </template>
            <span>{{ hints.backgroundColor }}</span>
          </v-tooltip>
        </div>
        <div class="color-picker-row">
          <input
            type="color"
            class="color-swatch"
            :value="modelValue.backgroundColor"
            aria-label="Background colour"
            :disabled="readonly"
            @input="update('backgroundColor', $event.target.value)"
          />
          <v-text-field
            :model-value="modelValue.backgroundColor"
            variant="outlined"
            density="compact"
                hide-details
            class="color-hex-input"
            placeholder="#ffffff"
            :readonly="readonly"
            @update:model-value="update('backgroundColor', $event)"
          />
        </div>
      </div>

      <div class="color-picker-group">
        <div class="block-label-row">
          <label class="block-label">Foreground colour</label>
          <v-tooltip location="top" max-width="280">
            <template #activator="{ props }">
              <v-icon
                v-bind="props"
                icon="mdi-help-circle-outline"
                size="small"
                class="field-help-icon"
                aria-label="Foreground colour help"
              />
            </template>
            <span>{{ hints.foregroundColor }}</span>
          </v-tooltip>
        </div>
        <div class="color-picker-row">
          <input
            type="color"
            class="color-swatch"
            :value="modelValue.foregroundColor"
            aria-label="Foreground colour"
            :disabled="readonly"
            @input="update('foregroundColor', $event.target.value)"
          />
          <v-text-field
            :model-value="modelValue.foregroundColor"
            variant="outlined"
            density="compact"
                hide-details
            class="color-hex-input"
            placeholder="#000000"
            :readonly="readonly"
            @update:model-value="update('foregroundColor', $event)"
          />
        </div>
      </div>
    </div>

    <!-- Threshold -->
    <div class="field-block threshold-block">
      <div class="threshold-header">
        <div class="block-label-row">
          <label class="block-label">Stamps to earn a reward</label>
          <v-tooltip location="top" max-width="280">
            <template #activator="{ props }">
              <v-icon
                v-bind="props"
                icon="mdi-help-circle-outline"
                size="small"
                class="field-help-icon"
                aria-label="Stamps threshold help"
              />
            </template>
            <span>{{ hints.threshold }}</span>
          </v-tooltip>
        </div>
        <span class="threshold-value" aria-live="polite">
          {{ thresholdValue }}
        </span>
      </div>
      <v-slider
        :model-value="thresholdValue"
        :min="6"
        :max="14"
        :step="1"
        show-ticks="always"
        tick-size="3"
        thumb-label
        track-color="rgba(0, 109, 62, 0.2)"
        hide-details
        aria-label="Stamps to earn a reward"
        :disabled="readonly"
        @update:model-value="updateThreshold($event)"
      />
    </div>

    <!-- Start stamp count (dashboard settings only - the registration flow
         doesn't surface it, so a new business starts at the 0 default). -->
    <div v-if="showStartStampCount" class="field-block threshold-block">
      <div class="threshold-header">
        <div class="block-label-row">
          <label class="block-label">Start stamp count</label>
          <v-tooltip location="top" max-width="280">
            <template #activator="{ props }">
              <v-icon
                v-bind="props"
                icon="mdi-help-circle-outline"
                size="small"
                class="field-help-icon"
                aria-label="Start stamp count help"
              />
            </template>
            <span>{{ hints.startStampCount }}</span>
          </v-tooltip>
        </div>
        <span class="threshold-value" aria-live="polite">
          {{ startStampValue }}
        </span>
      </div>
      <v-slider
        :model-value="startStampValue"
        :min="0"
        :max="startStampMax"
        :step="1"
        show-ticks="always"
        tick-size="3"
        thumb-label
        track-color="rgba(0, 109, 62, 0.2)"
        hide-details
        aria-label="Start stamp count"
        :disabled="readonly"
        @update:model-value="updateNumber('startStampCount', $event)"
      />
    </div>

    <!-- Auxiliary field (optional) -->
    <fieldset class="field-block aux-block">
      <legend class="aux-legend">
        <span>Optional extras</span>
        <v-tooltip location="top" max-width="280">
          <template #activator="{ props }">
            <v-icon
              v-bind="props"
              icon="mdi-help-circle-outline"
              size="small"
              class="field-help-icon"
              aria-label="Optional extras help"
            />
          </template>
          <span>Add an extra line to your card - e.g. your Instagram handle. Leave both blank to hide it.</span>
        </v-tooltip>
      </legend>
      <div class="aux-fields">
        <v-text-field
          :model-value="modelValue.auxiliaryFieldLabel"
          label="Heading (optional)"
          variant="outlined"
          density="comfortable"
          :readonly="readonly"
          @update:model-value="update('auxiliaryFieldLabel', $event)"
        >
          <template #append-inner>
            <v-tooltip location="top" max-width="280">
              <template #activator="{ props }">
                <v-icon
                  v-bind="props"
                  icon="mdi-help-circle-outline"
                  size="small"
                  class="field-help-icon"
                  aria-label="Heading help"
                />
              </template>
              <span>{{ hints.auxLabel }}</span>
            </v-tooltip>
          </template>
        </v-text-field>
        <v-text-field
          :model-value="modelValue.auxiliaryFieldValue"
          label="Content (optional)"
          variant="outlined"
          density="comfortable"
          :readonly="readonly"
          @update:model-value="update('auxiliaryFieldValue', $event)"
        >
          <template #append-inner>
            <v-tooltip location="top" max-width="280">
              <template #activator="{ props }">
                <v-icon
                  v-bind="props"
                  icon="mdi-help-circle-outline"
                  size="small"
                  class="field-help-icon"
                  aria-label="Content help"
                />
              </template>
              <span>{{ hints.auxValue }}</span>
            </v-tooltip>
          </template>
        </v-text-field>
      </div>
      <p v-if="showAuxValidation && auxPairError" class="aux-pair-error" role="alert">
        {{ auxPairError }}
      </p>
    </fieldset>
  </div>
</template>

<script>
import { computed } from "vue";
import LogoUploader from "@/components/Molecules/LogoUploader.vue";
import { required, threshold as thresholdRule } from "@/utils/passwordRules.js";

export default {
  name: "BrandingEditor",
  components: { LogoUploader },
  emits: ["update:modelValue", "update:logoFile", "logo-error"],
  props: {
    // Bound branding slice. Field names match what `saveBusinessRegistration`
    // and the M5 `updateRewardsCardBranding` Cloud Function consume.
    modelValue: {
      type: Object,
      required: true,
    },
    // Optional remote logo URL (for the dashboard editor - registration
    // path uses the in-memory preview generated from the selected file).
    initialLogoUrl: {
      type: String,
      default: "",
    },
    // Optional file name to seed the displayed filename. Used by the
    // registration stepper to keep the original filename visible when the
    // user navigates back to the branding step after uploading.
    initialLogoFileName: {
      type: String,
      default: "",
    },
    // When true, every input is rendered in a non-interactive mode. We
    // use Vuetify's `:readonly` on text/select inputs (still focusable,
    // still copyable, screen-reader announces the value) and `:disabled`
    // on the file input + slider because there's no equivalent readonly
    // affordance for those primitives. This is preferable to
    // `pointer-events:none` because it preserves keyboard navigation and
    // accessibility tree semantics.
    readonly: {
      type: Boolean,
      default: false,
    },
    // Hide the display-name input. The registration stepper collects it
    // in the previous step, so showing it again would be a duplicate.
    hideDisplayName: {
      type: Boolean,
      default: false,
    },
    // Hide the logo picker. The registration stepper collects the logo on
    // its Business basics step (via LogoUploader directly).
    hideLogo: {
      type: Boolean,
      default: false,
    },
    // External validation error for the logo (e.g. "please upload a logo"
    // when the parent's continue check fails). Internal upload errors
    // (file too large, etc.) take priority since they're more specific.
    validationError: {
      type: String,
      default: "",
    },
    // Whether to surface the aux-pair validation error. The registration
    // stepper holds this back until the user taps Continue; dashboard
    // usage validates eagerly.
    showAuxValidation: {
      type: Boolean,
      default: true,
    },
    // Show the "Start stamp count" slider. Off by default so the
    // registration stepper keeps its existing set of fields - only the
    // dashboard Settings page opts in.
    showStartStampCount: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const hints = {
      displayName:
        "Your business name exactly as customers should see it. This shows on the rewards card and your sign-up page.",
      backgroundColor:
        "Your card's background colour. Click the coloured square to open the picker, or type a hex code directly. The preview updates live.",
      foregroundColor:
        "Colour of the text and stamps. Click the coloured square to open the picker, or type a hex code directly. Make sure it contrasts strongly with the background so stamps are easy to see.",
      threshold:
        "How many stamps a customer collects before earning a reward (e.g. 9 = the 10th visit earns the reward). 6-14 - a lower number rewards customers sooner.",
      startStampCount:
        "Free stamps every new customer starts with when they sign up. Customers visit more often the closer they get to a reward, so a small head start brings them back sooner. Leave at 0 to start everyone from empty.",
      auxLabel:
        "Optional - the heading shown above the extra line on the card, e.g. 'Instagram'.",
      auxValue:
        "Optional - the text shown beneath the heading, e.g. '@yourhandle'. Leave both blank to hide this line.",
    };

    const rules = { required, threshold: thresholdRule };

    const update = (key, value) => {
      emit("update:modelValue", { ...props.modelValue, [key]: value });
    };

    const updateNumber = (key, value) => {
      const numeric = value === "" || value == null ? "" : Number(value);
      emit("update:modelValue", { ...props.modelValue, [key]: numeric });
    };

    // The threshold is the ceiling of the start-stamp range, so lowering it
    // has to pull an out-of-range start count down with it - otherwise the
    // slider would render clamped while the model still held (and saved) the
    // larger number.
    const updateThreshold = (value) => {
      const numeric = value === "" || value == null ? "" : Number(value);
      const next = { ...props.modelValue, thresholdForRedeeming: numeric };
      const start = Number(props.modelValue.startStampCount);
      if (Number.isFinite(numeric) && Number.isFinite(start) && start > numeric) {
        next.startStampCount = numeric;
      }
      emit("update:modelValue", next);
    };

    // The aux heading/content pair must either both be filled or both
    // blank - partial entries would render an empty value (or labelless
    // value) on the card, which looks broken.
    const auxPairError = computed(() => {
      const hasLabel = !!(props.modelValue.auxiliaryFieldLabel || "").trim();
      const hasValue = !!(props.modelValue.auxiliaryFieldValue || "").trim();
      if (hasLabel !== hasValue) {
        return "Fill in both heading and content, or leave both blank.";
      }
      return "";
    });

    // Display value for the slider. The model can carry a string (from
    // text inputs upstream) or a number - coerce defensively so the
    // slider always sees a number and the thumb-label reads cleanly.
    const thresholdValue = computed(() => {
      const raw = props.modelValue.thresholdForRedeeming;
      const n = Number(raw);
      if (!Number.isFinite(n)) return 8;
      if (n < 6) return 6;
      if (n > 14) return 14;
      return n;
    });

    // The start-stamp slider tops out at the redeem threshold - a business
    // can hand out at most a full card's worth of free stamps.
    const startStampMax = computed(() => thresholdValue.value);

    const startStampValue = computed(() => {
      const n = Number(props.modelValue.startStampCount);
      if (!Number.isFinite(n) || n < 0) return 0;
      return Math.min(n, startStampMax.value);
    });

    return {
      hints,
      rules,
      update,
      updateNumber,
      updateThreshold,
      thresholdValue,
      startStampMax,
      startStampValue,
      auxPairError,
    };
  },
};
</script>

<style scoped>
.branding-editor {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.field-block {
  display: flex;
  flex-direction: column;
}

.block-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--lulu-primary);
  margin-bottom: 4px;
}

.block-label-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}

.block-label-row .block-label {
  margin-bottom: 0;
}

.field-help-icon {
  color: var(--lulu-text-muted);
  opacity: 0.7;
  cursor: help;
  transition: color 0.15s, opacity 0.15s;
}

.field-help-icon:hover {
  opacity: 1;
  color: var(--lulu-primary);
}

.block-hint {
  font-size: 12px;
  color: var(--lulu-text-muted);
  margin: 0 0 10px;
  line-height: 1.5;
}

.color-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

@media (max-width: 560px) {
  .color-row {
    grid-template-columns: 1fr;
  }
}

.color-picker-group {
  padding: 16px;
  background: var(--lulu-surface);
  border-radius: 12px;
  border: 1px solid var(--lulu-border);
}

.color-picker-row {
  display: flex;
  gap: 10px;
  align-items: center;
}

.color-swatch {
  width: 50px;
  height: 40px;
  border: 1px solid var(--lulu-border);
  border-radius: 6px;
  cursor: pointer;
  padding: 0;
  background: transparent;
}

.color-hex-input {
  flex-grow: 1;
}

.threshold-block {
  padding: 16px 18px 14px;
  background: var(--lulu-surface);
  border-radius: 12px;
  border: 1px solid var(--lulu-border);
}

.threshold-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
}

.threshold-header .block-label-row {
  margin-bottom: 0;
}

.threshold-value {
  font-size: 22px;
  font-weight: 800;
  color: var(--lulu-primary);
  line-height: 1;
  min-width: 36px;
  text-align: right;
}

.threshold-hint {
  margin-top: 6px;
}

.aux-block {
  border: 1px solid var(--lulu-border);
  border-radius: 12px;
  padding: 16px 18px 6px;
  background: var(--lulu-surface-soft);
}

.aux-legend {
  font-size: 13px;
  font-weight: 700;
  color: var(--lulu-text-strong);
  padding: 0 6px;
  margin-left: -6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.aux-block-hint {
  margin: 4px 0 12px;
}

.aux-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

@media (max-width: 560px) {
  .aux-fields {
    grid-template-columns: 1fr;
  }
}

.aux-pair-error {
  color: var(--lulu-danger);
  font-size: 12px;
  margin: 8px 0 0;
  font-weight: 500;
}
</style>
